import { View } from "@frui.ts/views/dist";
import Button from "@edaz/edazkit/Button/index";
import HeaderRow from "@edaz/edazkit/HeaderRow/index";
import { IconType } from "@edaz/edazkit/Icon";
import { observer, Observer } from "mobx-react-lite";
import Alert from "react-bootstrap/Alert";
import ButtonToolbar from "react-bootstrap/ButtonToolbar";
import type ListViewModel from "viewModels/listViewModel";
import { DataTable } from "@frui.ts/dataviews/dist";
import type { ColumnDefinition, PropertyKey } from "@frui.ts/dataviews/dist/dataTypes";
import LoaderOverlay from "@edaz/edazkit/LoaderOverlay/index";
import { isDateProperty, isNumberProperty } from "utils/propertyUtils";
import { isFilterEmpty } from "utils/filters";
import Paginator from "./paginator";
import React from "react";
import { globalBusyWatcher } from "../../utils/busyWatcher";
import Tooltip from "@edaz/edazkit/Tooltip";

export interface ListViewProps<TEntity> {
  vm: ListViewModel<TEntity>;
  classNames?: string;
  style?: string;
  columns: ListColumnDefinition<TEntity, any>[];
  title?: string;
  aboveTable?: React.ReactNode;
  underTable?: React.ReactNode;
  headerButtons?: React.ReactNode;
  actionButtons?: (item: TEntity) => React.ReactNode;
  showDetailWithList?: boolean;
  hideTitle?: boolean;
  hideButtons?: boolean;
  forceHeaderButtons?: boolean;
  mandatoryFilter?: boolean;
  disabledAddButton?: boolean;
  disabledAddButtonTooltip?: string;
  customColumnDefinitionFunction?: (props: ListViewProps<TEntity>) => ColumnDefinition<TEntity>[];
  rowProps?: (item: TEntity, context: any) => React.ComponentPropsWithoutRef<"tr">;
}

export interface ListColumnDefinition<TItem, TContext = any, TProperty extends PropertyKey<TItem> = PropertyKey<TItem>>
  extends ColumnDefinition<TItem, TContext, TProperty> {
  dateProperty?: boolean;
  numberProperty?: boolean;
}

export function listColumns<TEntity>(props: ListViewProps<TEntity>) {
  const { actionButtons, vm, columns, hideButtons } = props;
  const onItemClick = vm.onItemClick();
  const onDeleteClick = vm.onDeleteClick();
  const buttonsColumns: ColumnDefinition<TEntity>[] = [];
  const modifiedColumns = columns.map(column => {
    if (
      (column.property && (isDateProperty(column.property) || isNumberProperty(column.property))) ||
      column.dateProperty ||
      column.numberProperty
    ) {
      return {
        cellClassName: "text-nowrap text-right",
        headerClassName: "text-right",
        ...column,
      };
    }

    return column;
  });

  if (!hideButtons && (onItemClick || onDeleteClick || actionButtons)) {
    buttonsColumns.push({
      cellClassName: "sticky",
      headerClassName: "sticky",
      valueFormatter: props => (
        <ButtonToolbar className="d-flex flex-nowrap justify-content-end">
          {actionButtons?.(props.item)}
          {onItemClick && vm.allowItemDetail(props.item) && (
            <Button
              variant="success"
              iconLeft={vm.allowWrite ? IconType.Edit : IconType.Eye}
              size="sm"
              className="rounded"
              onClick={() => onItemClick(props.item)}
            />
          )}
          {onDeleteClick && vm.allowItemDelete(props.item) && (
            <Button
              iconLeft={IconType.Trash}
              size="sm"
              variant="danger"
              className="rounded"
              onClick={() => onDeleteClick(props.item)}
            />
          )}
        </ButtonToolbar>
      ),
    });
  }

  return [...modifiedColumns, ...buttonsColumns];
}

function ListView<TEntity>(props: ListViewProps<TEntity>) {
  const {
    vm,
    rowProps,
    headerButtons,
    forceHeaderButtons,
    aboveTable,
    underTable,
    classNames,
    title,
    mandatoryFilter,
    disabledAddButton,
    disabledAddButtonTooltip,
    customColumnDefinitionFunction,
  } = props;
  const onAddClick = vm.onAddClick();
  const columnsProps = customColumnDefinitionFunction ? customColumnDefinitionFunction(props) : listColumns<TEntity>(props);
  if (vm.activeChild && !props.showDetailWithList) {
    return <Observer>{() => <View vm={vm.activeChild} />}</Observer>;
  }

  return (
    <>
      {!props.hideTitle && (
        <HeaderRow level={3} title={title ?? vm.name} className="mb-4">
          {(onAddClick || headerButtons) && (
            <ButtonToolbar>
              {onAddClick &&
                !forceHeaderButtons &&
                (disabledAddButton ? (
                  <Tooltip
                    id="vehicle-register-vehicle-warning"
                    trigger={["hover", "focus"]}
                    tooltip={disabledAddButtonTooltip}
                    placement="left">
                    <Button className="rounded" size="sm" disabled iconLeft={IconType.SignPlus} onClick={onAddClick}>
                      {vm.translateGeneral("add")}
                    </Button>
                  </Tooltip>
                ) : (
                  <Button className="rounded" size="sm" iconLeft={IconType.SignPlus} onClick={onAddClick}>
                    {vm.translateGeneral("add")}
                  </Button>
                ))}

              {headerButtons}
            </ButtonToolbar>
          )}
        </HeaderRow>
      )}
      <LoaderOverlay loading={vm.busyWatcher.checkBusy(globalBusyWatcher)} className="mb-4">
        {aboveTable}
        {mandatoryFilter && isFilterEmpty(vm.appliedFilter) ? (
          <Alert variant="info" className="mt-6">
            {vm.translate("toStartFilter")}
          </Alert>
        ) : (
          <>
            <div className={`${classNames} table-full`} style={{ overflowX: "auto" }}>
              <DataTable
                columns={columnsProps}
                items={vm.items}
                itemKey={vm.idProperty}
                className="table table-striped mb-0 text-nowrap"
                rowProps={rowProps}
              />
              {vm.busyWatcher.isBusy && vm.items.length === 0 && (
                <div className="bg-white mt-n2 px-4 py-3 border-bottom text-muted">{vm.translateGeneral("loading")}</div>
              )}
              {!vm.busyWatcher.isBusy && vm.items.length === 0 && (
                <div className="bg-white mt-n2 px-4 py-3 border-bottom text-muted">{vm.translateGeneral("empty")}</div>
              )}
            </div>
            <Paginator
              className="justify-content-center mt-4 mb-2"
              pagingInfo={vm.currentPaging}
              filter={vm.pagingFilter}
              onPageChanged={() => vm.loadData()}
            />
          </>
        )}
        {underTable}
        {vm.activeChild && <Observer>{() => <View vm={vm.activeChild} />}</Observer>}
      </LoaderOverlay>
    </>
  );
}

export default observer(ListView);
